import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ManagerNavbar from "../Layout/ManagerNavbar";
import { parseISO, isWithinInterval, addDays } from "date-fns";
import {
  FaTasks, FaClock, FaUsers, FaChartBar, FaStopwatch,
  FaArrowRight, FaCalendarCheck, FaCalendarTimes,
} from "react-icons/fa";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import TermsConditionsPopup from "../components/TermsConditionsPopup";
import API_BASE_URL from "../config";

/* ─────────────────────────────────────────────
   Design tokens
───────────────────────────────────────────── */
const C = {
  bg: "#f0f2f8",
  surface: "#ffffff",
  navy: "#1a2744",
  navyMid: "#243460",
  indigo: "#3b5bdb",
  indigoLight: "#eef2ff",
  teal: "#0ca678",
  tealLight: "#e6f9f3",
  amber: "#f59f00",
  amberLight: "#fff9db",
  coral: "#f03e3e",
  coralLight: "#fff0f0",
  violet: "#7048e8",
  violetLight: "#f3f0ff",
  slate: "#64748b",
  border: "#e2e8f0",
  text: "#1e293b",
  muted: "#94a3b8",
};

const PIE_COLORS = [C.coral, C.indigo, C.teal, C.amber];

/* ─────────────────────────────────────────────
   Google Font inject (one-time)
───────────────────────────────────────────── */
if (!document.getElementById("mgr-font")) {
  const link = document.createElement("link");
  link.id = "mgr-font";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=DM+Sans:wght@400;500&display=swap";
  document.head.appendChild(link);
}

/* ─────────────────────────────────────────────
   Keyframes (one-time)
───────────────────────────────────────────── */
if (!document.getElementById("mgr-keyframes")) {
  const style = document.createElement("style");
  style.id = "mgr-keyframes";
  style.textContent = `
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes shimmer {
      0%   { background-position: -400px 0; }
      100% { background-position:  400px 0; }
    }
    .mgr-card {
      animation: fadeUp 0.45s ease both;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .mgr-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 36px rgba(26,39,68,0.14) !important;
    }
    .mgr-chip {
      display: inline-flex; align-items: center; gap: 4px;
      font-size: 11px; font-weight: 600; letter-spacing: 0.4px;
      padding: 3px 8px; border-radius: 20px;
    }
    .mgr-badge-pill {
      display: inline-block; padding: 2px 10px;
      border-radius: 99px; font-size: 12px; font-weight: 600;
    }
    .summary-row:hover { background: #f8fafc; border-radius: 8px; }
    .mgr-nav-link { transition: background 0.15s; border-radius: 8px; }
    .mgr-nav-link:hover { background: rgba(255,255,255,0.08); }
    .recharts-tooltip-wrapper { font-family: 'DM Sans', sans-serif !important; }

    /* ── Scoped scrollbar ── */
    .mgr-scroll::-webkit-scrollbar { width: 6px; }
    .mgr-scroll::-webkit-scrollbar-track { background: transparent; }
    .mgr-scroll::-webkit-scrollbar-thumb { background: #c5cfe0; border-radius: 99px; }
    .mgr-scroll::-webkit-scrollbar-thumb:hover { background: #a0b0c8; }

    @media (max-width: 1100px) {
      .stat-grid { grid-template-columns: repeat(3, 1fr) !important; }
      .chart-grid { grid-template-columns: 1fr !important; }
    }
    @media (max-width: 640px) {
      .stat-grid { grid-template-columns: repeat(2, 1fr) !important; }
    }
  `;
  document.head.appendChild(style);
}

/* ─────────────────────────────────────────────
   Custom Tooltip
───────────────────────────────────────────── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: C.navy, color: "#fff", borderRadius: 10,
      padding: "10px 16px", fontSize: 13, fontFamily: "'DM Sans', sans-serif",
      boxShadow: "0 6px 24px rgba(0,0,0,0.22)",
    }}>
      {label && <div style={{ opacity: 0.7, marginBottom: 4, fontSize: 11 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ fontWeight: 600 }}>
          <span style={{ color: p.color }}>{p.name}: </span>{p.value}
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Stat Card
───────────────────────────────────────────── */
const StatCard = ({ title, value, sub, icon, accent, accentBg, path, delay = 0 }) => {
  const navigate = useNavigate();
  return (
    <div
      className="mgr-card"
      onClick={() => path && navigate(path)}
      style={{
        background: C.surface,
        borderRadius: 16,
        padding: "22px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        boxShadow: "0 2px 12px rgba(26,39,68,0.07)",
        cursor: path ? "pointer" : "default",
        border: `1px solid ${C.border}`,
        position: "relative",
        overflow: "hidden",
        animationDelay: `${delay}ms`,
      }}
    >
      {/* accent strip */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        height: 3, background: accent, borderRadius: "16px 16px 0 0",
      }} />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{
          width: 46, height: 46, borderRadius: 12,
          background: accentBg, display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          {React.cloneElement(icon, { size: 20, color: accent })}
        </div>
        {path && (
          <FaArrowRight size={13} color={C.muted} style={{ marginTop: 4 }} />
        )}
      </div>

      <div>
        <div style={{
          fontSize: 28, fontWeight: 700, color: C.text,
          fontFamily: "'Sora', sans-serif", lineHeight: 1.1,
        }}>
          {value}
        </div>
        <div style={{ fontSize: 13, fontWeight: 500, color: C.slate, marginTop: 3 }}>
          {title}
        </div>
        {sub && (
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4 }}>{sub}</div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Section wrapper
───────────────────────────────────────────── */
const Section = ({ title, children, action }) => (
  <div style={{
    background: C.surface,
    borderRadius: 18,
    padding: "24px 28px",
    boxShadow: "0 2px 12px rgba(26,39,68,0.07)",
    border: `1px solid ${C.border}`,
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
      <h3 style={{ margin: 0, fontFamily: "'Sora', sans-serif", fontSize: 16, fontWeight: 700, color: C.text }}>
        {title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: "Manager" });
  const [loading, setLoading] = useState(true);

  const [teamMembers, setTeamMembers]           = useState(0);
  const [pendingLeaves, setPendingLeaves]       = useState(0);
  const [dashboardData, setDashboardData]       = useState({ leaves: [] });
  const [upcomingLeaves, setUpcomingLeaves]     = useState(0);
  const [ongoingTasks, setOngoingTasks]         = useState(0);
  const [Timesheet, setTimesheet]               = useState(0);
  const [financialYear, setFinancialYear]       = useState("");
  const [remainingLeaves, setRemainingLeaves]   = useState(0);
  const [leaveSummary, setLeaveSummary]         = useState([]);

  const userId    = localStorage.getItem("employeeId");
  const managerId = localStorage.getItem("employeeId");
  const token     = localStorage.getItem("token");

  /* ── Company info ── */
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(r => r.json())
      .then(data => {
        const company = Array.isArray(data) ? data[0] : data;
        if (company?.financialYear) setFinancialYear(company.financialYear);
      })
      .catch(console.error);
  }, []);

  /* ── Auth + metrics ── */
  useEffect(() => {
    const roles  = JSON.parse(localStorage.getItem("roles") || "[]");
    const status = localStorage.getItem("status") || "";
    if (!token || status.toUpperCase() !== "ACTIVE" || !roles.includes("ROLE_MANAGER")) {
      navigate("/"); return;
    }
    const userId2 = localStorage.getItem("userId");

    fetch(`${API_BASE_URL}/api/users/${userId2}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setProfile({ name: d.name || "Manager" }))
      .catch(() => {});

    fetch(`${API_BASE_URL}/api/manageteam/reporter/${managerId}`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(r => r.json())
      .then(data => {
        const arr    = Array.isArray(data) ? data : [];
        const unique = Array.from(new Map(arr.map(i => [i.employeeId, i])).values());
        setTeamMembers(unique.length);
      })
      .catch(console.error);

    fetch(`${API_BASE_URL}/api/tasks/employee/${managerId}/pending-count`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setOngoingTasks(d.pendingCount || 0));

    // Timesheets
    fetch(`${API_BASE_URL}/api/manageteam/reporter/${managerId}`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(r => r.json())
      .then(async teamData => {
        const arr         = Array.isArray(teamData) ? teamData : [];
        const employeeIds = [...new Set(arr.filter(i => i?.employeeId).map(i => i.employeeId))];
        const results     = await Promise.all(
          employeeIds.map(id =>
            fetch(`${API_BASE_URL}/api/worklogs/employee/${id}`, {
              headers: { Authorization: "Bearer " + token },
            }).then(r => r.json()).catch(() => [])
          )
        );
        const allLogs   = results.flat();
        const now       = new Date();
        const startPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const filtered  = allLogs.filter(l => {
          const d = new Date(l.date);
          return d >= startPrev && d <= now;
        });
        setTimesheet(new Set(filtered.map(l => l.employeeId)).size);
      })
      .catch(console.error);

    setLoading(false);
  }, [navigate]);

  /* ── Leaves ── */
  useEffect(() => {
    if (!financialYear) return;
    fetch(
      `${API_BASE_URL}/api/leave-transactions/team-avail?financialYear=${financialYear}`,
      { headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" } }
    )
      .then(r => r.json())
      .then(data => {
        const leaves = (Array.isArray(data) ? data : []).map(l => ({ ...l, status: l.leaveStatus }));
        const now    = new Date();
        const in7    = addDays(now, 7);
        setUpcomingLeaves(leaves.filter(l =>
          l.status === "APPROVED" && l.startDate &&
          isWithinInterval(parseISO(l.startDate), { start: now, end: in7 })
        ).length);
        setPendingLeaves(leaves.filter(l => l.status === "PENDING").length);
        setDashboardData({ leaves });
      })
      .catch(console.error);
  }, [financialYear]);

  /* ── Leave summary ── */
  useEffect(() => {
    if (!financialYear) return;
    Promise.all([
      axios.get(`${API_BASE_URL}/api/leave-types/all`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API_BASE_URL}/api/leave-transactions/summary/${userId}`, {
        params: { financialYear },
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(([, summaryRes]) => {
        const summaryData = Array.isArray(summaryRes.data) ? summaryRes.data : [];
        setLeaveSummary(summaryData);
        setRemainingLeaves(summaryData.reduce((s, i) => s + (i.remaining || 0), 0));
      })
      .catch(console.error);
  }, [financialYear]);

  if (loading) return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: C.bg, fontFamily: "'Sora', sans-serif", color: C.slate, fontSize: 15,
    }}>
      Loading dashboard…
    </div>
  );

  const pieData = [
    { name: "Pending",  value: pendingLeaves  },
    { name: "Upcoming", value: upcomingLeaves },
  ];

  const barData = leaveSummary.map(i => ({
    name:      i.leaveType?.length > 10 ? i.leaveType.slice(0, 10) + "…" : i.leaveType,
    Remaining: i.remaining,
    Applied:   i.applied,
  }));

  const CARDS = [
    {
      title: "Team Members", value: teamMembers, sub: "Active reporters",
      icon: <FaUsers />, accent: C.indigo, accentBg: C.indigoLight,
      path: "/teampage", delay: 0,
    },
    {
      title: "Tasks In To-Do", value: ongoingTasks, sub: "Pending tasks",
      icon: <FaTasks />, accent: C.teal, accentBg: C.tealLight,
      path: "/viewtaskss", delay: 60,
    },
    {
      title: "Upcoming Leaves", value: upcomingLeaves, sub: "Next 7 days (approved)",
      icon: <FaCalendarCheck />, accent: C.amber, accentBg: C.amberLight,
      path: "/managerteamleaves", delay: 120,
    },
    {
      title: "Pending Approvals", value: pendingLeaves, sub: "Awaiting your action",
      icon: <FaCalendarTimes />, accent: C.coral, accentBg: C.coralLight,
      path: "/pendingmanagerteamleaves", delay: 180,
    },
    {
      title: "Remaining Leaves", value: remainingLeaves, sub: `FY ${financialYear}`,
      icon: <FaStopwatch />, accent: C.violet, accentBg: C.violetLight, delay: 240,
    },
    {
      title: "Timesheets Submitted", value: Timesheet, sub: "Last 30 days",
      icon: <FaChartBar />, accent: C.navyMid, accentBg: "#eef0f8",
      path: "/teamtimesheetgrid", delay: 300,
    },
  ];

  return (
    <>
      <TermsConditionsPopup />
      <div style={{
        height: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: C.bg,
        fontFamily: "'DM Sans', sans-serif",
        boxSizing: "border-box",
      }}>
        {/* Fixed Navbar */}
        <div style={{ flexShrink: 0, position: "relative", zIndex: 1000 }}>
          <ManagerNavbar />
        </div>

        {/* Scrollable content area */}
        <div
          className="mgr-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {/* Inner padding wrapper */}
          <div style={{ width: "100%", boxSizing: "border-box", padding: "10px 36px 52px" }}>

            {/* ── Header ── */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 32, animation: "fadeUp 0.4s ease both",
            }}>
              <div>
                <h1 style={{
                  margin: "4px 0 0", fontSize: 26, fontWeight: 700,
                  fontFamily: "'Sora', sans-serif", color: C.text,
                }}>
                  👋 Welcome back, {profile.name}
                </h1>
              </div>
              <div style={{
                background: C.navy, color: "#fff",
                padding: "8px 18px", borderRadius: 24,
                fontSize: 13, fontWeight: 600, letterSpacing: "0.3px",
                fontFamily: "'Sora', sans-serif",
              }}>
                FY {financialYear || "—"}
              </div>
            </div>

            {/* ── Stat cards ── */}
            <div className="stat-grid" style={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 18,
              marginBottom: 32,
            }}>
              {CARDS.map((c, i) => <StatCard key={i} {...c} />)}
            </div>

            {/* ── Bottom row ── */}
            <div className="chart-grid" style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.2fr 1.6fr",
              gap: 22,
              alignItems: "start",
            }}>

              {/* Pie chart */}
              <Section title="Leave Status">
                {pieData.every(p => p.value === 0) ? (
                  <div style={{ textAlign: "center", color: C.muted, padding: "30px 0", fontSize: 13 }}>
                    No leave data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={55}
                        outerRadius={88}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {pieData.map((_, idx) => (
                          <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        iconType="circle"
                        iconSize={8}
                        wrapperStyle={{ fontSize: 12, fontFamily: "'DM Sans', sans-serif" }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div style={{ display: "flex", gap: 10, marginTop: 10, justifyContent: "center" }}>
                  <Pill label="Pending"  value={pendingLeaves}  color={C.coral} bg={C.coralLight} />
                  <Pill label="Upcoming" value={upcomingLeaves} color={C.indigo} bg={C.indigoLight} />
                </div>
              </Section>

              {/* Bar chart */}
              <Section title="Leave Balance by Type">
                {barData.length === 0 ? (
                  <div style={{ textAlign: "center", color: C.muted, padding: "30px 0", fontSize: 13 }}>
                    No leave types found
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={barData} barSize={12} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f8" />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Remaining" fill={C.teal}  radius={[4, 4, 0, 0]} />
                      <Bar dataKey="Applied"   fill={C.coral} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Section>

              {/* Leave Summary Table */}
              <Section
                title="My Leave Summary"
                action={
                  <span style={{ fontSize: 11, color: C.muted }}>
                    Remaining <span style={{ color: C.slate }}>[Applied]</span>
                  </span>
                }
              >
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {leaveSummary.length === 0 ? (
                    <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>
                      No leave data
                    </div>
                  ) : leaveSummary.map((item, i) => {
                    const pct = item.total > 0 ? Math.round((item.remaining / item.total) * 100) : 0;
                    return (
                      <div key={i} className="summary-row" style={{
                        display: "flex", flexDirection: "column", gap: 5,
                        padding: "10px 8px", borderBottom: `1px solid ${C.border}`,
                      }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontSize: 13.5, color: C.text, fontWeight: 500 }}>
                            {item.leaveType}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                            {item.remaining}
                            <span style={{ fontWeight: 400, color: C.muted, fontSize: 12 }}> [{item.applied}]</span>
                          </span>
                        </div>
                        {/* progress bar */}
                        <div style={{ height: 4, background: C.border, borderRadius: 99, overflow: "hidden" }}>
                          <div style={{
                            height: "100%", width: `${pct}%`,
                            background: pct > 50 ? C.teal : pct > 20 ? C.amber : C.coral,
                            borderRadius: 99, transition: "width 0.6s ease",
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total row */}
                {leaveSummary.length > 0 && (
                  <div style={{
                    display: "flex", justifyContent: "space-between",
                    marginTop: 14, paddingTop: 12, borderTop: `2px solid ${C.border}`,
                    fontWeight: 700, fontSize: 14, color: C.text,
                    fontFamily: "'Sora', sans-serif",
                  }}>
                    <span>Total</span>
                    <span>
                      {leaveSummary.reduce((s, i) => s + i.remaining, 0)}
                      <span style={{ fontWeight: 500, color: C.muted, fontSize: 13 }}>
                        {" "}[{leaveSummary.reduce((s, i) => s + i.applied, 0)}]
                      </span>
                    </span>
                  </div>
                )}
              </Section>

            </div>
          </div>{/* end inner padding wrapper */}
        </div>{/* end mgr-scroll */}
      </div>
    </>
  );
};

/* ── Pill helper ── */
const Pill = ({ label, value, color, bg }) => (
  <div style={{
    display: "flex", alignItems: "center", gap: 6,
    background: bg, borderRadius: 99, padding: "4px 12px",
  }}>
    <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
    <span style={{ fontSize: 12, color, fontWeight: 600 }}>{label}</span>
    <span style={{ fontSize: 12, fontWeight: 700, color }}>{value}</span>
  </div>
);

export default ManagerDashboard;